// @flow

import * as React from 'react';

import { useDispatch } from 'react-redux';

import { useTranslator } from '@gandi/translate';
import { Dialog, DialogHeader, type DialogStateProps, Text } from '@gandi/ui';

import { useAppSelector } from 'src/hooks/useAppSelector';

import { setPhase } from '../../modules/actions/results';

import { ConfirmationStep } from './ConfirmationStep';
import { PhaseSelectionStep } from './PhaseSelectionStep';
import { selectModalState, setModalState, resetModalState } from './module';

type Props = {
  dialog: DialogStateProps,
  onBulkCancel: () => void,
};

function PhasesDialog({
  dialog,
  onBulkCancel,
}: Props): React.MixedElement | null {
  const dispatch = useDispatch();
  const { __ } = useTranslator();
  const {
    callback,
    callbackCancel,
    fqdn,
    phase: modalPhase,
    currentPage,
    totalPage,
  } = useAppSelector(selectModalState);
  const [confirmationStep, setConfirmationStep] = React.useState(false);

  const isLast =
    currentPage === undefined ||
    totalPage === undefined ||
    currentPage === totalPage;

  const close = (isCancel: boolean = true): void => {
    dialog.close();
    // we test the last item if a bulk
    // works as well if it is the only one
    if (isLast || isCancel) {
      // $FlowFixMe[not-an-array]
      dispatch(resetModalState());
      setConfirmationStep(false);

      if (isCancel) onBulkCancel();
    } else {
      // $FlowFixMe[not-an-array]
      dispatch(setModalState({ phase: undefined }));
      // reopen with CSS anim
      setTimeout(() => {
        dialog.show();
        setConfirmationStep(false);
      }, 0);
    }
  };
  const dialogState = {
    ...dialog,
    close,
  };

  const handleConfirmation = async () => {
    // set the phase to suggestion results
    // $FlowFixMe[not-an-array]
    await dispatch(setPhase({ fqdn, phase: modalPhase }));

    if (typeof callback === 'function') {
      dispatch(callback);
    }
    close(false);
  };

  const handleCancel = (evt: SyntheticMouseEvent<>): void => {
    evt.preventDefault();

    if (typeof callbackCancel === 'function') {
      callbackCancel();
    }

    close(true);
  };

  // when phases dialog does not have enough data, we should not render
  // we could check other values from the modalState
  // however fqdn seems enough
  // it specificaly avoid the translator to crash
  if (!fqdn) {
    return null;
  }

  return (
    <Dialog {...dialogState}>
      <DialogHeader>
        {__('Register %(fqdn)s', { fqdn })}{' '}
        {currentPage !== undefined && totalPage !== undefined && (
          <Text color="light">
            {currentPage} / {totalPage}
          </Text>
        )}
      </DialogHeader>
      {confirmationStep && modalPhase ? (
        <ConfirmationStep
          handleCancel={handleCancel}
          handleConfirmation={handleConfirmation}
          setConfirmationStep={setConfirmationStep}
          phase={modalPhase}
        />
      ) : (
        <PhaseSelectionStep
          handleCancel={handleCancel}
          handleConfirmation={handleConfirmation}
          setConfirmationStep={setConfirmationStep}
        />
      )}
    </Dialog>
  );
}

export default PhasesDialog;
